import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { isEqual } from 'lodash';
import React from 'react';
import {
    GridEventListener,
    GridRowEditStartReasons,
    GridRowEditStopReasons,
    GridRowId,
    GridRowModel,
    GridRowModes,
    useGridApiRef,
} from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import { errorCellStyling, useAuth } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessLocker } from '@barboxx/models';

import { LockerNfcUrlsDialog } from './LockerNfcUrlsDialog';
import { buildLockerColumns } from './LockerTableColumns';
import { generateLockerNfcCredentials } from './nfcCredentials';

// Generated by Copilot

interface Props {
    lockers: BusinessLocker[];
    refetch: () => void;
}

export function LockerTable({ lockers, refetch }: Props) {
    const notifications = useNotifications();
    const apiRef = useGridApiRef();
    const [selectedLockerForNfcUrls, setSelectedLockerForNfcUrls] =
        React.useState<BusinessLocker | null>(null);
    const {
        auth: { user },
    } = useAuth();
    const isInternalUser = user.userType === 'Internal';

    const mutateBusinessLocker = useMutation({
        mutationKey: ['businessLockerUpdate'],
        mutationFn: ({ id, ...rest }: Omit<BusinessLocker, 'businessId'>) =>
            Api.BusinessLockers.updateLocker(id, {
                ...rest,
                id,
            }),
        onSuccess: data => {
            notifications.show(`Updated Locker`, {
                severity: 'success',
                autoHideDuration: 1000,
            });
        },
    });

    const handleRowEditStart: GridEventListener<'rowEditStart'> = (
        params,
        event
    ) => {
        // disable double click to edits
        event.defaultMuiPrevented = true;

        if (params.reason === GridRowEditStartReasons.cellDoubleClick) {
            return;
        }

        apiRef.current?.startRowEditMode({
            id: params.id,
            fieldToFocus: params.field,
        });
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (
        params,
        event
    ) => {
        // disable click outside save
        event.defaultMuiPrevented = true;

        switch (params.reason) {
            case GridRowEditStopReasons.rowFocusOut:
                apiRef.current?.stopRowEditMode({
                    id: params.id,
                    ignoreModifications: true,
                });
                break;
            default:
                apiRef.current?.stopRowEditMode({
                    id: params.id,
                });
                break;
        }
    };

    const processRowUpdate = React.useCallback(
        async (
            newRow: GridRowModel<BusinessLocker>,
            oldRow: GridRowModel<BusinessLocker>
        ) => {
            if (isEqual(newRow, oldRow)) {
                return oldRow;
            }

            const response = await mutateBusinessLocker.mutateAsync(newRow);

            return {
                ...newRow,
                ...response,
            };
        },
        [mutateBusinessLocker]
    );

    const handleProcessRowUpdateError = React.useCallback(
        (error: Error) => {
            notifications.show(`Error: ${error.message}`, {
                severity: 'error',
                autoHideDuration: 1000,
            });
        },
        [notifications]
    );

    const handleOpenNfcUrlList = React.useCallback((locker: BusinessLocker) => {
        setSelectedLockerForNfcUrls(locker);
    }, []);

    const handleCloseNfcUrlList = React.useCallback(() => {
        setSelectedLockerForNfcUrls(null);
    }, []);

    const handleGenerateNfcCredentials = React.useCallback(
        (id: GridRowId) => {
            const gridApi = apiRef.current;

            if (!gridApi) {
                return;
            }

            const { nfcPassword, nfcSecret } = generateLockerNfcCredentials();

            void gridApi.setEditCellValue({
                id,
                field: 'nfcPassword',
                value: nfcPassword,
            });
            void gridApi.setEditCellValue({
                id,
                field: 'nfcSecret',
                value: nfcSecret,
            });

            notifications.show('Generated NFC credentials for this locker', {
                severity: 'info',
                autoHideDuration: 1200,
            });
        },
        [apiRef, notifications]
    );

    const isRowInEditMode = React.useCallback(
        (id: GridRowId) => apiRef.current?.getRowMode(id) === GridRowModes.Edit,
        [apiRef]
    );

    const columns = React.useMemo(
        () =>
            buildLockerColumns({
                isInternalUser,
                isRowInEditMode,
                onGenerateNfcCredentials: handleGenerateNfcCredentials,
                onOpenNfcUrlList: handleOpenNfcUrlList,
                onDelete: refetch,
            }),
        [
            handleGenerateNfcCredentials,
            handleOpenNfcUrlList,
            isInternalUser,
            isRowInEditMode,
            refetch,
        ]
    );

    return (
        <>
            <DataGridCard title="Lockers">
                <DataGrid
                    columns={columns}
                    editMode="row"
                    onRowEditStart={handleRowEditStart}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    apiRef={apiRef}
                    sx={errorCellStyling}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    rows={lockers}
                    rowCount={lockers.length}
                />
            </DataGridCard>
            <LockerNfcUrlsDialog
                open={!!selectedLockerForNfcUrls}
                locker={selectedLockerForNfcUrls}
                onClose={handleCloseNfcUrlList}
            />
        </>
    );
}
